import type { PropsWithChildren } from "react";

import type { IconName } from "@similarweb/icons";

import { translate } from "app/filters/i18nFilter";

import type {
    ICampaignLocationAndLanguage,
    ILocationDetailsToPresent,
} from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import { DEVICES_TYPES_FOR_API } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

export enum ESearchEngineDevice {
    DESKTOP,
    MOBILE,
    COMBINED,
}

export const deviceEnumToNameMap: Record<ESearchEngineDevice, DEVICES_TYPES_FOR_API> = {
    [ESearchEngineDevice.DESKTOP]: DEVICES_TYPES_FOR_API.DESKTOP,
    [ESearchEngineDevice.MOBILE]: DEVICES_TYPES_FOR_API.MOBILE,
    [ESearchEngineDevice.COMBINED]: DEVICES_TYPES_FOR_API.COMBINED,
};

export const deviceTitlesMap = new Map<ESearchEngineDevice, string>([
    [ESearchEngineDevice.DESKTOP, "seo.keywordsCampaign.wizard.searchEnginesStep.inputs.device.desktop"],
    [ESearchEngineDevice.MOBILE, "seo.keywordsCampaign.wizard.searchEnginesStep.inputs.device.mobile"],
    [ESearchEngineDevice.COMBINED, "seo.keywordsCampaign.wizard.searchEnginesStep.inputs.device.both"],
]);

export const getDeviceTitlesMap = (): Map<ESearchEngineDevice, string> =>
    new Map([...deviceTitlesMap.entries()].map(([device, key]) => [device, translate(key)]));

export enum EditMode {
    Draft, // initial
    Saved,
    Edit,
}

export interface ICampaignsSearchEngineCompany {
    id: string;
    text: string;
    iconName: IconName;
}

export interface ICampaignsLanguage {
    id: string;
    text: string;
    code?: string;
}

export interface ISearchEngineDetails {
    id: string;
    key?: string | number;
    location?: ILocationDetailsToPresent;
    company?: ICampaignsSearchEngineCompany;
    device?: ESearchEngineDevice;
    language?: ICampaignsLanguage;
}

export interface IEditableSearchEngine {
    searchEngineDetails: ISearchEngineDetails;
    editMode: EditMode;
}

export interface ISearchEngineBaseItemProps {
    searchEngineList: ISearchEngineDetails[];
    onDelete?: (se: ISearchEngineDetails) => void;
    onSave?: (se: ISearchEngineDetails) => void;
    locationsAndLanguagesLists?: ICampaignLocationAndLanguage;
}

interface ISearchEngineBaseItemBase {
    searchEngine: IEditableSearchEngine;
    onSaveItemComplete?: (searchEngine: ISearchEngineDetails) => void;
    isSaved?: boolean;
}

export interface ISearchEngineItemEditModeProps extends PropsWithChildren<ISearchEngineBaseItemBase> {
    locationsAndLanguagesLists: ICampaignLocationAndLanguage;
    cancelItem?: (searchEngine: IEditableSearchEngine) => void;
    searchEngineItems: IEditableSearchEngine[];
    onRemoveSearchEngine?: (searchEngine: ISearchEngineDetails) => void;
    duplicateItem?: (searchEngine: ISearchEngineDetails) => void;
    disableOptions?: boolean;
}

export interface ISearchEngineItemViewModeProps extends ISearchEngineBaseItemBase {
    handleEditItem?: (searchEngineID: string) => void;
    onRemoveSearchEngine?: (searchEngine: ISearchEngineDetails) => void;
    handleSaveItem?: (searchEngine: ISearchEngineDetails) => void;
    setCurrentEditedSearchEngineID?: (number) => void;
    handleSetCompany?: (ICampaignsListItemSearchEngineCompany) => void;
    handleSetDevice?: (ESearchEngineDevice) => void;
    handleSetLocation?: (ICountryDropdownItem) => void;
    handleSetLanguage?: (ICountryDropdownItem) => void;
    duplicateItem: (searchEngine: ISearchEngineDetails) => void;
    removeIcon?: IconName;
}

export interface ICampaignsWizardSearchEngineTypeInput {
    searchEngine: ISearchEngineDetails;
    handleSetCompany?: (ICampaignsListItemSearchEngineCompany) => void;
    handleSetDevice?: (ESearchEngineDevice) => void;
    handleSetLocationAndResetLanguage?: (ICountryDropdownItem) => void;
    handleSetLanguage?: (ICampaignsLanguage) => void;
}
export interface ICampaignsWizardSearchEngineLocationInput extends ICampaignsWizardSearchEngineTypeInput {
    locationsAndLanguagesLists: ICampaignLocationAndLanguage;
}

export interface ICampaignsListItemSearchEngineCompany {
    id: string;
    onClick?: (event) => void;
    selected?: boolean;
    disabled?: boolean;
    text?: string;
    iconName?: IconName;
}

// The only available search engine ATM
export const availableSearchEngineCompany: ICampaignsSearchEngineCompany = {
    id: "google",
    text: "Google",
    iconName: "google",
};

export interface IRemoveSearchEngineModalProps {
    isOpen?: boolean;
    onClose: () => void;
    onRemoveApprove: () => void;
}

export const SynonymousCountriesNamesMap = new Map<string, string>([
    ["Czechia", "Czech Republic"],
    ["Turkiye", "Turkey"],
]);
